import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, ExternalLink, FileDown, FileText } from "lucide-react"
import {
  usePortalAuth,
  usePortalDocuments,
  usePortalInvoices,
  usePortalUnbilled,
} from "@/hooks/usePortalAuth"
import { portalApiBlob } from "@/lib/portal-api"
import { invoiceStatusChipClass } from "@/lib/chip-styles"
import { getPortalInvoiceDisplayState } from "@/lib/invoice-payment"
import { getOpenEndedEngagementLabel } from "@/lib/engagement-dates"
import { usePageTitle } from "@/hooks/usePageTitle"
import { track } from "@/lib/posthog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { OverflowTooltip } from "@/components/ui/overflow-tooltip"

function formatDate(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function formatCurrency(amount: number | null, currency: string) {
  if (amount === null || Number.isNaN(amount)) return "—"
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

function formatCurrencyBreakdown(rows: Array<{ currency: string; amount: number }>) {
  if (rows.length === 0) return "—"
  return rows.map((row) => formatCurrency(row.amount, row.currency)).join(" · ")
}

function formatTreatment(entry: {
  included_hours?: number
  cash_hours?: number
  tracked_hours?: number
}) {
  const treatments: string[] = []
  if (entry.included_hours) treatments.push(`${entry.included_hours.toFixed(2)}h included`)
  if (entry.cash_hours) treatments.push(`${entry.cash_hours.toFixed(2)}h paid`)
  if (entry.tracked_hours) treatments.push(`${entry.tracked_hours.toFixed(2)}h tracked`)
  return treatments.join(" · ") || "—"
}

export function PortalDashboard() {
  const { data: me, isLoading: meLoading } = usePortalAuth()
  const { data: unbilled, isLoading: unbilledLoading } = usePortalUnbilled()
  const { data: invoices, isLoading: invoicesLoading } = usePortalInvoices()
  const { data: documents, isLoading: documentsLoading } = usePortalDocuments()
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  usePageTitle(me?.client.name ? `${me.client.name} · Client Portal` : "Client Portal")

  const totals = useMemo(() => {
    if (!unbilled) return null
    const visibleInvoices = (invoices || []).filter((invoice) => invoice.status !== "void")
    const invoiceTotals = new Map<string, { invoiced: number; paid: number }>()
    for (const invoice of visibleInvoices) {
      const current = invoiceTotals.get(invoice.currency) || { invoiced: 0, paid: 0 }
      current.invoiced += Number(invoice.total)
      current.paid += Number(invoice.amount_paid || 0)
      invoiceTotals.set(invoice.currency, current)
    }
    if (invoiceTotals.size === 0) {
      invoiceTotals.set(unbilled.currency, { invoiced: 0, paid: 0 })
    }

    return {
      hours: unbilled.total_hours,
      accruedByCurrency: unbilled.totals_by_currency.map((total) => ({
        currency: total.currency,
        amount: total.accrued_amount,
      })),
      deferredByCurrency: unbilled.totals_by_currency.map((total) => ({
        currency: total.currency,
        amount: total.deferred_amount,
      })),
      invoicedByCurrency: [...invoiceTotals].map(([currency, total]) => ({
        currency,
        amount: total.invoiced,
      })),
      paidByCurrency: [...invoiceTotals].map(([currency, total]) => ({
        currency,
        amount: total.paid,
      })),
    }
  }, [invoices, unbilled])

  async function handleDownload(invoiceId: string) {
    setDownloadingId(invoiceId)
    try {
      const blob = await portalApiBlob(`/api/portal/invoices/${invoiceId}/pdf`)
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      const selected = invoices?.find((invoice) => invoice.id === invoiceId)
      link.download = `Invoice_${selected?.invoice_number || "invoice"}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      track("portal_invoice_downloaded", { invoice_id: invoiceId })
    } catch (error) {
      console.error("Failed to download invoice", error)
    } finally {
      setDownloadingId(null)
    }
  }

  if (meLoading || unbilledLoading || invoicesLoading || documentsLoading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading portal...</p>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen overflow-x-clip">
      <div className="mx-auto w-full max-w-7xl space-y-6 px-3 py-6 sm:px-6 sm:py-10 lg:px-8">
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm">{me?.billing_entity_name || "Billing"}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-1">
              <h1 className="text-3xl font-semibold [overflow-wrap:anywhere]">{me?.client.name}</h1>
              <p className="text-muted-foreground text-sm [overflow-wrap:anywhere]">
                Portal access for {me?.portal_email || me?.client.billing_email || "your email"}
              </p>
            </div>
            {me && me.accessible_clients.length > 1 ? (
              <Button asChild variant="outline">
                <Link to="/select">Switch Vendor</Link>
              </Button>
            ) : null}
          </div>
        </div>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>Accrued Work</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 space-y-6 px-4 sm:px-6">
            <div className="grid min-w-0 grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-5">
              <div className="min-w-0">
                <p className="text-muted-foreground text-sm">Accrued hours</p>
                <p className="text-2xl font-semibold whitespace-nowrap tabular-nums">
                  {totals ? totals.hours.toFixed(2) : "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-sm">Accrued cash</p>
                <p className="text-2xl leading-tight font-semibold break-words tabular-nums">
                  {totals ? formatCurrencyBreakdown(totals.accruedByCurrency) : "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-sm">Deferred cash</p>
                <p className="text-2xl leading-tight font-semibold break-words tabular-nums">
                  {totals ? formatCurrencyBreakdown(totals.deferredByCurrency) : "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-sm">Invoiced</p>
                <p className="text-2xl leading-tight font-semibold break-words tabular-nums">
                  {totals ? formatCurrencyBreakdown(totals.invoicedByCurrency) : "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-muted-foreground text-sm">Paid</p>
                <p className="text-2xl leading-tight font-semibold break-words tabular-nums">
                  {totals ? formatCurrencyBreakdown(totals.paidByCurrency) : "—"}
                </p>
              </div>
            </div>

            {unbilled && unbilled.engagements.length > 0 ? (
              <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
                {unbilled.engagements.map((engagement) => (
                  <div
                    key={engagement.id}
                    className="max-w-full min-w-0 space-y-2 overflow-hidden rounded-lg border p-4"
                  >
                    <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-3 gap-y-2">
                      <p className="min-w-0 flex-1 text-sm leading-snug font-medium [overflow-wrap:anywhere]">
                        {engagement.name}
                      </p>
                      <Badge variant="outline" className="shrink-0 capitalize">
                        {engagement.status}
                      </Badge>
                    </div>
                    {engagement.start_date ? (
                      <p className="text-muted-foreground flex flex-wrap gap-x-1 text-xs leading-relaxed">
                        <span className="whitespace-nowrap">
                          {formatDate(engagement.start_date)}
                        </span>
                        <span aria-hidden="true">–</span>
                        <span className="whitespace-nowrap">
                          {engagement.end_date
                            ? formatDate(engagement.end_date)
                            : getOpenEndedEngagementLabel(engagement.status)}
                        </span>
                      </p>
                    ) : null}
                    {engagement.plan ? (
                      <p className="text-muted-foreground text-xs leading-relaxed break-words">
                        Plan {engagement.plan.planned_min_hours ?? 0}–
                        {engagement.plan.planned_max_hours ?? 0} hours per{" "}
                        {engagement.plan.planning_period || "term"}
                      </p>
                    ) : null}
                    {engagement.compensation ? (
                      <p className="text-muted-foreground text-xs leading-relaxed break-words">
                        {engagement.compensation.included_hours > 0
                          ? `${engagement.compensation.included_hours} included hours per ${engagement.compensation.allowance_period} · `
                          : ""}
                        {formatCurrency(
                          engagement.compensation.tiers.find((tier) => tier.treatment === "cash")
                            ?.hourly_rate || 0,
                          engagement.compensation.currency
                        )}
                        /hour{engagement.compensation.included_hours > 0 ? " after" : ""}
                      </p>
                    ) : null}
                    {engagement.compensation?.payment_events.find(
                      (event) => event.status === "pending"
                    ) ? (
                      <p className="text-muted-foreground text-xs leading-relaxed break-words">
                        Payment becomes ready after{" "}
                        {
                          engagement.compensation.payment_events.find(
                            (event) => event.status === "pending"
                          )?.label
                        }
                        .
                      </p>
                    ) : null}
                    {engagement.included_usage ? (
                      <p className="text-muted-foreground text-xs leading-relaxed break-words tabular-nums">
                        {engagement.included_usage.used_hours.toFixed(2)} of{" "}
                        {engagement.included_usage.allowance_hours.toFixed(2)} included hours used
                      </p>
                    ) : null}
                    {engagement.amounts_by_currency.length > 0 ? (
                      <p className="text-sm break-words tabular-nums">
                        {formatCurrencyBreakdown(
                          engagement.amounts_by_currency.map((amount) => ({
                            currency: amount.currency,
                            amount: amount.accrued_amount,
                          }))
                        )}{" "}
                        accrued
                        {engagement.amounts_by_currency.some((amount) => amount.deferred_amount > 0)
                          ? ` · ${formatCurrencyBreakdown(
                              engagement.amounts_by_currency.map((amount) => ({
                                currency: amount.currency,
                                amount: amount.deferred_amount,
                              }))
                            )} deferred`
                          : ""}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {unbilled && unbilled.accrued_charges.length > 0 ? (
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-medium">Accrued charges</h3>
                  <p className="text-muted-foreground text-xs">
                    Fixed and milestone charges that have not moved to an issued invoice.
                  </p>
                </div>
                <div className="hidden max-w-full overflow-x-auto md:block">
                  <table className="w-full min-w-[48rem] table-fixed text-sm">
                    <colgroup>
                      <col className="w-32" />
                      <col className="w-56" />
                      <col />
                      <col className="w-28" />
                      <col className="w-32" />
                    </colgroup>
                    <thead>
                      <tr className="text-muted-foreground border-b text-left">
                        <th className="py-3 pr-6 whitespace-nowrap">Service date</th>
                        <th className="py-3 pr-8 whitespace-nowrap">Engagement</th>
                        <th className="py-3 pr-6">Charge</th>
                        <th className="py-3 pr-4 whitespace-nowrap">Status</th>
                        <th className="py-3 text-right whitespace-nowrap">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unbilled.accrued_charges.map((charge) => (
                        <tr key={charge.id} className="border-b last:border-0">
                          <td className="py-3 pr-6 align-top whitespace-nowrap">
                            <time dateTime={charge.service_date}>
                              {formatDate(charge.service_date)}
                            </time>
                          </td>
                          <td className="py-3 pr-8 align-top">
                            <OverflowTooltip
                              text={charge.engagement_name}
                              className="truncate whitespace-nowrap"
                            />
                          </td>
                          <td className="py-3 pr-6 align-top leading-relaxed break-words">
                            <OverflowTooltip text={charge.label} />
                          </td>
                          <td className="py-3 pr-4 align-top whitespace-nowrap">
                            <Badge variant="outline" className="capitalize">
                              {charge.readiness}
                            </Badge>
                          </td>
                          <td className="py-3 text-right align-top whitespace-nowrap tabular-nums">
                            {formatCurrency(charge.amount, charge.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 md:hidden">
                  {unbilled.accrued_charges.map((charge) => (
                    <div key={charge.id} className="min-w-0 space-y-2 rounded-lg border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <time
                          dateTime={charge.service_date}
                          className="text-muted-foreground text-sm whitespace-nowrap"
                        >
                          {formatDate(charge.service_date)}
                        </time>
                        <span className="shrink-0 text-sm font-medium whitespace-nowrap tabular-nums">
                          {formatCurrency(charge.amount, charge.currency)}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed break-words">{charge.label}</p>
                      <div className="flex min-w-0 flex-wrap items-end justify-between gap-2">
                        <p className="text-muted-foreground min-w-0 flex-1 text-xs break-words">
                          {charge.engagement_name}
                        </p>
                        <Badge variant="outline" className="shrink-0 capitalize">
                          {charge.readiness}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {unbilled && unbilled.entries.length > 0 ? (
              <>
                <div className="hidden max-w-full overflow-x-auto lg:block">
                  <table className="w-full min-w-[56rem] table-fixed text-sm">
                    <colgroup>
                      <col className="w-28" />
                      <col className="w-60" />
                      <col />
                      <col className="w-36" />
                      <col className="w-20" />
                      <col className="w-28" />
                    </colgroup>
                    <thead>
                      <tr className="text-muted-foreground border-b text-left">
                        <th className="py-3 pr-6 whitespace-nowrap">Date</th>
                        <th className="py-3 pr-10 whitespace-nowrap">Engagement</th>
                        <th className="py-3 pr-6">Task</th>
                        <th className="py-3 pr-6 whitespace-nowrap">Treatment</th>
                        <th className="py-3 text-right whitespace-nowrap">Hours</th>
                        <th className="py-3 pl-5 text-right whitespace-nowrap">Accrued</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unbilled.entries.map((entry) => (
                        <tr key={entry.id} className="border-b last:border-0">
                          <td className="py-3 pr-6 align-top whitespace-nowrap">
                            <time dateTime={entry.date}>{formatDate(entry.date)}</time>
                          </td>
                          <td className="py-3 pr-10 align-top">
                            <OverflowTooltip
                              text={entry.engagement_name || "—"}
                              className="truncate whitespace-nowrap"
                            />
                          </td>
                          <td className="py-3 pr-6 align-top leading-relaxed break-words">
                            <OverflowTooltip text={entry.task} />
                          </td>
                          <td className="text-muted-foreground py-3 pr-6 align-top text-xs">
                            <OverflowTooltip
                              text={formatTreatment(entry)}
                              className="truncate whitespace-nowrap"
                            />
                          </td>
                          <td className="py-3 text-right align-top whitespace-nowrap tabular-nums">
                            {entry.hours.toFixed(2)}
                          </td>
                          <td className="py-3 pl-5 text-right align-top whitespace-nowrap tabular-nums">
                            {entry.accrued_amount == null
                              ? "—"
                              : formatCurrency(
                                  entry.accrued_amount,
                                  entry.currency || unbilled.currency
                                )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 lg:hidden">
                  {unbilled.entries.map((entry) => (
                    <article key={entry.id} className="min-w-0 space-y-3 rounded-lg border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <time
                          dateTime={entry.date}
                          className="text-muted-foreground text-sm whitespace-nowrap"
                        >
                          {formatDate(entry.date)}
                        </time>
                        <span className="shrink-0 text-sm font-medium whitespace-nowrap tabular-nums">
                          {entry.hours.toFixed(2)}h
                        </span>
                      </div>
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm leading-relaxed font-medium [overflow-wrap:anywhere]">
                          {entry.task}
                        </p>
                        {entry.engagement_name ? (
                          <p className="text-muted-foreground text-xs leading-relaxed [overflow-wrap:anywhere]">
                            {entry.engagement_name}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex min-w-0 flex-wrap items-end justify-between gap-2 border-t pt-2">
                        <p className="text-muted-foreground min-w-0 flex-1 text-xs break-words">
                          {formatTreatment(entry)}
                        </p>
                        <span className="shrink-0 text-sm whitespace-nowrap tabular-nums">
                          {entry.accrued_amount == null
                            ? "—"
                            : formatCurrency(
                                entry.accrued_amount,
                                entry.currency || unbilled.currency
                              )}
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            ) : unbilled && unbilled.accrued_charges.length === 0 ? (
              <p className="text-muted-foreground text-sm">No work is currently accruing.</p>
            ) : null}
          </CardContent>
        </Card>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>Invoice History</CardTitle>
          </CardHeader>
          <CardContent className="min-w-0 space-y-5 px-4 sm:px-6">
            {invoices && invoices.length > 0 ? (
              invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="grid min-w-0 grid-cols-1 gap-4 border-b pb-5 last:border-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
                >
                  <div className="min-w-0">
                    <p className="font-medium [overflow-wrap:anywhere]">{invoice.invoice_number}</p>
                    <div className="text-muted-foreground mt-1 flex flex-col gap-0.5 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2">
                      <span className="whitespace-nowrap">
                        {invoice.document_kind === "legacy_credit" ? "Recorded" : "Issued"}{" "}
                        {formatDate(invoice.issue_date)}
                      </span>
                      <span className="hidden sm:inline" aria-hidden="true">
                        •
                      </span>
                      <span
                        className={
                          invoice.document_kind === "legacy_credit"
                            ? undefined
                            : "whitespace-nowrap"
                        }
                      >
                        {invoice.document_kind === "legacy_credit"
                          ? "Read-only history"
                          : `Due ${formatDate(invoice.due_date)}`}
                      </span>
                    </div>
                    <div className="mt-2">
                      <Badge variant="outline" className={invoiceStatusChipClass(invoice.status)}>
                        {getPortalInvoiceDisplayState(invoice).statusLabel}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex min-w-0 flex-col gap-3 sm:items-end">
                    <p className="text-lg font-semibold whitespace-nowrap tabular-nums">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </p>
                    <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto">
                      <Button asChild size="sm" className="w-full sm:w-auto">
                        <Link to={`/invoices/${invoice.id}`} state={{ fromPortalDashboard: true }}>
                          {invoice.document_kind === "legacy_credit"
                            ? "View credit"
                            : "View invoice"}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => void handleDownload(invoice.id)}
                        disabled={downloadingId === invoice.id}
                      >
                        <FileDown className="h-4 w-4" />
                        {downloadingId === invoice.id ? "Generating..." : "PDF"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm">No invoices yet.</p>
            )}
          </CardContent>
        </Card>

        {documents && documents.length > 0 ? (
          <Card className="min-w-0 overflow-hidden">
            <CardHeader className="px-4 sm:px-6">
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent className="min-w-0 divide-y px-4 sm:px-6">
              {documents.map((document) => (
                <a
                  key={document.id}
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:bg-muted/40 -mx-3 flex items-center justify-between gap-3 rounded-lg px-3 py-3 transition-colors"
                  onClick={() =>
                    track("portal_document_opened", {
                      document_id: document.id,
                      document_type: document.document_type,
                    })
                  }
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="bg-muted rounded-lg p-2">
                      <FileText className="text-muted-foreground h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{document.title}</span>
                      <span className="text-muted-foreground block text-xs capitalize">
                        {document.document_type.replaceAll("_", " ")}
                      </span>
                    </span>
                  </span>
                  <ExternalLink className="text-muted-foreground h-4 w-4 shrink-0" />
                </a>
              ))}
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  )
}
